import { useState, useEffect } from "react";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrg } from "@/hooks/useOrg";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Trash2, UserPlus, Pencil, Check, X, CreditCard, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface OrgMember {
  id: string;
  org_id: string;
  user_id: string | null;
  role: string;
  accepted_at: string | null;
  created_at: string;
  invited_email: string | null;
  full_name: string | null;
  email: string | null;
}

interface SubscriptionInfo {
  subscribed: boolean;
  tier: string;
  subscription_end: string | null;
  cancel_at_period_end?: boolean;
}

function PlanSection({ subInfo, isOwner, orgId }: { subInfo: SubscriptionInfo | undefined; isOwner: boolean; orgId: string }) {
  const [loading, setLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.get("upgraded") === "true") {
      queryClient.invalidateQueries({ queryKey: ["subscription", orgId] });
      toast.success("Welcome to Pro! 🎉");
      searchParams.delete("upgraded");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, queryClient, orgId, setSearchParams]);

  const handleUpgrade = async (priceId: string, mode: "subscription" | "payment") => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { orgId, priceId, mode },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (err: any) {
      toast.error(err.message || "Failed to start checkout");
    } finally {
      setLoading(false);
      setUpgradeOpen(false);
    }
  };

  const handleManage = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { orgId },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (err: any) {
      toast.error(err.message || "Failed to open billing portal");
    } finally {
      setLoading(false);
    }
  };

  const isPro = subInfo?.tier === "pro";

  if (!isOwner) return null;

  return (
    <div className="mb-8 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground">Plan</h2>
              <Badge variant={isPro ? "default" : "secondary"} className="text-xs">
                {isPro ? "Pro" : "Free"}
              </Badge>
              {isPro && subInfo?.cancel_at_period_end && subInfo?.subscription_end && (
                <span className="text-xs text-destructive">
                  · Expires {new Date(subInfo.subscription_end).toLocaleDateString()}
                </span>
              )}
              {isPro && !subInfo?.cancel_at_period_end && subInfo?.subscription_end && (
                <span className="text-xs text-muted-foreground">
                  · Renews {new Date(subInfo.subscription_end).toLocaleDateString()}
                </span>
              )}
              {!isPro && (
                <span className="text-xs text-muted-foreground">
                  · Upgrade to unlock all features
                </span>
              )}
            </div>
          </div>
        </div>
        {isPro ? (
          <Button variant="outline" size="sm" onClick={handleManage} disabled={loading}>
            <CreditCard className="h-4 w-4" />
            Manage subscription
          </Button>
        ) : (
          <Button size="sm" onClick={() => setUpgradeOpen(true)} disabled={loading}>
            <Sparkles className="h-4 w-4" />
            Upgrade to Pro
          </Button>
        )}
      </div>
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} onSelect={handleUpgrade} loading={loading} />
    </div>
  );
}

function MemberList({
  members,
  userId,
  removeMutation,
}: {
  members: OrgMember[];
  userId: string | undefined;
  removeMutation: ReturnType<typeof useMutation<void, Error, string>>;
}) {
  const getMemberDisplay = (member: OrgMember) => {
    if (member.full_name && member.email)
      return { primary: member.full_name, secondary: member.email };
    if (member.email) return { primary: member.email, secondary: null };
    if (member.invited_email) return { primary: member.invited_email, secondary: null };
    return { primary: "Unknown", secondary: null };
  };

  return (
    <div className="divide-y divide-border rounded-lg border border-border">
      {members.map((member) => {
        const display = getMemberDisplay(member);
        return (
          <div key={member.id} className="flex items-center gap-4 px-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {display.primary}
                {member.user_id === userId && (
                  <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                )}
              </p>
              {display.secondary && (
                <p className="text-xs text-muted-foreground truncate">{display.secondary}</p>
              )}
            </div>
            <Badge variant={member.role === "owner" ? "default" : "secondary"} className="text-xs">
              {member.role}
            </Badge>
            {!member.accepted_at && (
              <Badge variant="outline" className="text-xs">pending</Badge>
            )}
            {member.user_id !== userId && member.role !== "owner" && (
              <Button variant="ghost" size="icon" onClick={() => removeMutation.mutate(member.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Team() {
  const { orgId, org, refetch: refetchOrg } = useOrg();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [orgName, setOrgName] = useState("");

  const { data: members = [], isLoading } = useQuery<OrgMember[]>({
    queryKey: ["org_members", orgId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_org_members", { _org_id: orgId! });
      if (error) throw error;
      return data as OrgMember[];
    },
    enabled: !!orgId,
  });

  const { data: subInfo } = useQuery<SubscriptionInfo>({
    queryKey: ["subscription", orgId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        body: { orgId },
      });
      if (error) throw error;
      return data as SubscriptionInfo;
    },
    enabled: !!orgId,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (org) setOrgName(org.name);
  }, [org]);

  const isOwner = members.some((m) => m.user_id === user?.id && m.role === "owner");
  const isPro = subInfo?.tier === "pro";
  const canInvite = isOwner && isPro;

  const renameMutation = useMutation({
    mutationFn: async (newName: string) => {
      const { error } = await supabase.from("organizations").update({ name: newName }).eq("id", orgId!);
      if (error) throw error;
    },
    onSuccess: () => { refetchOrg(); setEditingName(false); toast.success("Organization renamed"); },
    onError: (err: Error) => toast.error(err.message),
  });

  const inviteMutation = useMutation({
    mutationFn: async (invitedEmail: string) => {
      // Send invite email
      const { error: emailError } = await supabase.functions.invoke("send-org-invite", {
        body: { invitedEmail, orgId },
      });
      if (emailError) throw emailError;

      // Add to org_members
      const { error: dbError } = await supabase.from("org_members").insert({ org_id: orgId!, invited_email: invitedEmail, role: "member" });
      if (dbError) throw dbError;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["org_members"] }); setEmail(""); toast.success("Invite sent"); },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase.from("org_members").delete().eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["org_members"] }); toast.success("Member removed"); },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("organizations")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", orgId!);
      if (error) throw error;
    },
    onSuccess: async () => {
      toast.success("Organization deleted");
      await refetchOrg();
      navigate("/dashboard");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleRename = (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim() || orgName.trim() === org?.name) { setEditingName(false); return; }
    renameMutation.mutate(orgName.trim());
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    inviteMutation.mutate(email.trim());
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          {editingName ? (
            <form onSubmit={handleRename} className="flex items-center gap-1.5">
              <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} className="h-8 text-lg font-semibold w-56" autoFocus />
              <Button type="submit" variant="ghost" size="icon" className="h-7 w-7"><Check className="h-4 w-4" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setOrgName(org?.name ?? ""); setEditingName(false); }}><X className="h-4 w-4" /></Button>
            </form>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-foreground">{org?.name ?? "Team"}</h1>
              {isOwner && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingName(true)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
        </div>
        <p className="text-sm text-muted-foreground">Manage who has access to your banners</p>
      </div>

      {orgId && <PlanSection subInfo={subInfo} isOwner={isOwner} orgId={orgId} />}

      {isOwner && (
        canInvite ? (
          <form onSubmit={handleInvite} className="mb-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="sm:max-w-xs" />
            <Button type="submit" size="sm" disabled={inviteMutation.isPending}><UserPlus className="h-4 w-4" />Invite</Button>
          </form>
        ) : (
          <div className="mb-8 rounded-lg border border-dashed border-border p-4 text-center">
            <p className="text-sm text-muted-foreground">Upgrade to Pro to invite team members</p>
          </div>
        )
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <MemberList members={members} userId={user?.id} removeMutation={removeMutation} />
      )}

      {isOwner && (
        <div className="mt-12 rounded-lg border border-destructive/30 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-destructive">Delete organization</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                This will permanently remove this organization and all its data.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete "{org?.name}"?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. All banners, members, and settings for this organization will be permanently removed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteMutation.mutate()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? "Deleting…" : "Delete organization"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}
    </div>
  );
}
